import { useEffect, useRef, useState } from "react";
import {
  IconDatabase,
  IconDownload,
  IconLanguage,
  IconLock,
  IconMail,
  IconMessage2,
  IconRefresh,
  IconSchool,
  IconShieldCheck,
  IconTrash,
  IconUpload,
  IconVolume,
} from "@tabler/icons-react";
import { Trans } from "@lingui/macro";

import { analytics } from "../../analytics/analyticsEvents";
import { appLinks, appVersion, feedbackFormUrl } from "../../app/appLinks";
import { getAnalyticsAdapter } from "../../analytics/AnalyticsAdapter";
import { declineAnalytics } from "../../analytics/consentActions";
import { localeLabels, locales } from "../../i18n";
import { useConsentStore } from "../../state/consentStore";
import { importSave, useGameStore } from "../../state/store";
import { downloadSave } from "../../state/saveFile";
import { useTutorialStore } from "../../state/tutorialStore";
import { Button } from "../common/Button";
import { Panel } from "../common/Panel";
import { cx } from "../common/cx";
import styles from "./SettingsScreen.module.css";

export function SettingsScreen(): JSX.Element {
  const language = useGameStore((state) => state.settings.language);
  const setLanguage = useGameStore((state) => state.setLanguage);
  const resetGame = useGameStore((state) => state.resetGame);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const inGame = useGameStore((state) => state.inGame);
  const consent = useConsentStore((state) => state.consent);
  const acceptConsent = useConsentStore((state) => state.accept);
  const declineConsent = useConsentStore((state) => state.decline);
  const restartTutorial = useTutorialStore((state) => state.start);
  const [confirmReset, setConfirmReset] = useState(false);
  const [importStatus, setImportStatus] = useState<"ok" | "error" | null>(null);
  const [pendingImport, setPendingImport] = useState<{ name: string; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    analytics.settingsOpened();
  }, []);

  const handleRestartTutorial = (): void => {
    restartTutorial();
    navigateTo("dashboard");
  };

  const handleImportFile = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    const text = await file.text();
    setImportStatus(null);
    setPendingImport({ name: file.name, text });
  };

  const confirmImport = (): void => {
    if (!pendingImport) {
      return;
    }
    setImportStatus(importSave(pendingImport.text) ? "ok" : "error");
    setPendingImport(null);
  };

  return (
    <div className={styles.screen}>
      {!inGame && (
        <div className={styles.backRow}>
          <Button variant="ghost" size="small" onClick={() => navigateTo("main_menu")}>
            <Trans>← В главное меню</Trans>
          </Button>
        </div>
      )}
      <Panel title={<Trans>Язык</Trans>} icon={<IconLanguage size={18} />}>
        <div className={styles.langRow}>
          {locales.map((locale) => (
            <Button
              key={locale}
              variant={locale === language ? "primary" : "secondary"}
              aria-pressed={locale === language}
              onClick={() => setLanguage(locale)}
            >
              {localeLabels[locale]}
            </Button>
          ))}
        </div>
      </Panel>

      <Panel title={<Trans>Звук</Trans>} icon={<IconVolume size={18} />}>
        <div className={styles.row}>
          <span className={styles.rowLabel}>
            <Trans>Звуковые эффекты</Trans>
          </span>
          <Button variant="secondary" size="small" disabled>
            <Trans>В разработке</Trans>
          </Button>
        </div>
      </Panel>

      <Panel title={<Trans>Данные сохранения</Trans>} icon={<IconDatabase size={18} />}>
        <p className={styles.hint}>
          <Trans>Скачай файл сейва для бэкапа или загрузи его на другом устройстве.</Trans>
        </p>
        <div className={styles.saveRow}>
          <Button variant="secondary" icon={<IconDownload size={16} />} onClick={downloadSave}>
            <Trans>Скачать сейв</Trans>
          </Button>
          <Button
            variant="secondary"
            icon={<IconUpload size={16} />}
            onClick={() => fileInputRef.current?.click()}
          >
            <Trans>Загрузить сейв</Trans>
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            className={styles.fileInput}
            onChange={handleImportFile}
          />
        </div>
        {pendingImport && (
          <div className={styles.importConfirm}>
            <span className={cx(styles.fileChip, "mono")}>
              <Trans>Выбран:</Trans> {pendingImport.name}
            </span>
            <p className={styles.importWarn}>
              <Trans>Импорт перезапишет текущий прогресс. Продолжить?</Trans>
            </p>
            <div className={styles.saveRow}>
              <Button variant="primary" size="small" icon={<IconUpload size={16} />} onClick={confirmImport}>
                <Trans>Импортировать</Trans>
              </Button>
              <Button variant="ghost" size="small" onClick={() => setPendingImport(null)}>
                <Trans>Отмена</Trans>
              </Button>
            </div>
          </div>
        )}
        {importStatus === "ok" && (
          <p className={styles.importOk} role="status">
            <Trans>Сейв загружен.</Trans>
          </p>
        )}
        {importStatus === "error" && (
          <p className={styles.importError} role="alert">
            <Trans>Не удалось прочитать файл сейва.</Trans>
          </p>
        )}
      </Panel>

      <Panel title={<Trans>Обучение</Trans>} icon={<IconSchool size={18} />}>
        <p className={styles.hint}>
          <Trans>Пройди вводное обучение на первом контракте заново.</Trans>
        </p>
        <div className={styles.saveRow}>
          <Button variant="secondary" icon={<IconSchool size={16} />} onClick={handleRestartTutorial}>
            <Trans>Пройти обучение заново</Trans>
          </Button>
        </div>
      </Panel>

      <Panel title={<Trans>Прогресс</Trans>} icon={<IconRefresh size={18} />}>
        <p className={styles.hint}>
          <Trans>Сброс удалит весь прогресс и вернёт в главное меню. Язык сохранится.</Trans>
        </p>
        <div className={cx(styles.resetRow)}>
          {confirmReset ? (
            <>
              <Button variant="danger" icon={<IconTrash size={16} />} onClick={() => resetGame()}>
                <Trans>Подтвердить сброс</Trans>
              </Button>
              <Button variant="ghost" onClick={() => setConfirmReset(false)}>
                <Trans>Отмена</Trans>
              </Button>
            </>
          ) : (
            <Button variant="secondary" icon={<IconTrash size={16} />} onClick={() => setConfirmReset(true)}>
              <Trans>Сбросить прогресс</Trans>
            </Button>
          )}
        </div>
      </Panel>

      <Panel title={<Trans>Конфиденциальность</Trans>} icon={<IconLock size={18} />}>
        <p className={styles.hint}>
          {consent === "denied" ? (
            <Trans>Внешняя аналитика отключена. Личные данные не собираются.</Trans>
          ) : (
            <Trans>
              Анонимная аналитика включена (PostHog, Sentry; серверы в ЕС). Личные данные не
              собираются.
            </Trans>
          )}
        </p>
        <div className={styles.saveRow}>
          <Button variant="secondary" size="small" onClick={() => navigateTo("privacy")}>
            <Trans>Политика конфиденциальности</Trans>
          </Button>
          {consent === "denied" ? (
            <Button variant="secondary" size="small" onClick={() => acceptConsent()}>
              <Trans>Включить аналитику</Trans>
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="small"
              onClick={() => declineAnalytics(getAnalyticsAdapter(), { decline: declineConsent })}
            >
              <Trans>Отказаться от аналитики</Trans>
            </Button>
          )}
        </div>
      </Panel>

      <Panel title={<Trans>Обратная связь</Trans>} icon={<IconMessage2 size={18} />}>
        <p className={styles.hint}>
          <Trans>
            Игра на этапе плейтеста. Расскажи, где было непонятно или скучно: это 5 коротких
            вопросов, анонимно.
          </Trans>
        </p>
        <div className={styles.saveRow}>
          <a
            className={styles.linkButton}
            href={feedbackFormUrl(appLinks, language)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconMessage2 size={16} aria-hidden="true" />
            <Trans>Оставить отзыв</Trans>
          </a>
          <a className={styles.linkButton} href={`mailto:${appLinks.supportEmail}`}>
            <IconMail size={16} aria-hidden="true" />
            <Trans>Написать в поддержку</Trans>
          </a>
        </div>
        <p className={cx(styles.systemLine, "mono")}>
          <Trans>Версия</Trans> {appVersion} · {appLinks.supportEmail}
        </p>
      </Panel>

      <Panel title={<Trans>О безопасности</Trans>} icon={<IconShieldCheck size={18} />}>
        <p className={styles.hint}>
          <Trans>
            Все компании, домены и события в игре вымышлены (домены вида *.example). Это
            обучающая симуляция в защитном ключе - никаких реальных атак или целей.
          </Trans>
        </p>
      </Panel>
    </div>
  );
}
